import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { initiateRegistration } from "../../../services/api"; // Import the new API service

// @mui material components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import GradientBorder from "examples/GradientBorder";
import VuiSwitch from "components/VuiSwitch";
import { IconButton, InputAdornment, Popover } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Vision UI Dashboard assets
import radialGradient from "assets/theme/functions/radialGradient";
import palette from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";

// Authentication layout components
import CoverLayout from "layouts/authentication/components/CoverLayout";

// Images
import bgSignIn from "assets/images/signUpImage.png";

function SignUp() {
  const [name, setName] = useState(localStorage.getItem("signUpName") || "");
  const [email, setEmail] = useState(localStorage.getItem("signUpEmail") || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accountType, setAccountType] = useState("personal"); // 'personal' or 'business'
  const [companyName, setCompanyName] = useState(localStorage.getItem("signUpCompanyName") || "");
  const [cnpj, setCnpj] = useState(localStorage.getItem("signUpCnpj") || "");
  const [cpf, setCpf] = useState(localStorage.getItem("signUpCpf") || "");
  const [error, setError] = useState(null);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    uppercase: false,
    specialChar: false,
    number: false,
  });
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("signUpName", name);
  }, [name]);

  useEffect(() => {
    localStorage.setItem("signUpEmail", email);
  }, [email]);

  useEffect(() => {
    localStorage.setItem("signUpCpf", cpf);
  }, [cpf]);

  useEffect(() => {
    localStorage.setItem("signUpCompanyName", companyName);
  }, [companyName]);

  useEffect(() => {
    localStorage.setItem("signUpCnpj", cnpj);
  }, [cnpj]);

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordRequirements({
      minLength: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      specialChar: /[!@#$%^&*]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("handleSubmit chamado!"); // Log para depuração
    if (password !== confirmPassword) {
      setError("As senhas não correspondem.");
      return;
    }
    setError(null); // Reset error before new submission
    try {
      const response = await initiateRegistration({ name, email, password, account_type: accountType, cpf, company_name: companyName, cnpj });
      if (response && response.user_id) {
        // Store user info if needed, then redirect
        // For example, you might want to pass the email to the subscribe page
        navigate("/dashboard", { replace: true });
      } else {
        throw new Error("Registration failed. Please try again.");
      }
    } catch (err) {
      setError(err.message || "An unknown error occurred.");
    }
  };

  return (
    <CoverLayout
      title="Bem-vindo!"
      color="white"
      description="Faça seu cadastro para começar."
      image={bgSignIn}
      premotto=""
      mottoLine1="SEU LANÇAMENTO PROTEGIDO:"
      mottoLine2="SAFERDOCK"
      cardContent
    >
      <GradientBorder borderRadius={borders.borderRadius.form} minWidth="100%" maxWidth="100%">
        <VuiBox
          component="form"
          role="form"
          borderRadius="inherit"
          p="25px"
          sx={({ palette: { secondary } }) => ({
            backgroundColor: secondary.focus,
          })}
          onSubmit={handleSubmit}
        >
          <VuiTypography
            color="white"
            fontWeight="bold"
            textAlign="center"
            mb="15px"
            sx={({ typography: { size } }) => ({
              fontSize: size.lg,
            })}
          >
            Crie sua conta
          </VuiTypography>
          <VuiBox mb={0.5}>
            <VuiBox mb={0.25} ml={0.5}>
              <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                Nome
              </VuiTypography>
            </VuiBox>
            <GradientBorder
              minWidth="100%"
              borderRadius={borders.borderRadius.lg}
              padding="1px"
              backgroundImage={radialGradient(
                palette.gradients.borderLight.main,
                palette.gradients.borderLight.state,
                palette.gradients.borderLight.angle
              )}
            >
              <VuiInput
                type="text"
                placeholder="Seu nome completo..."
                value={name}
                sx={({ typography: { size } }) => ({
                  fontSize: size.sm,
                })}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
              />
            </GradientBorder>
          </VuiBox>
          <VuiBox mb={0.5}>
            <VuiBox mb={0.25} ml={0.5}>
              <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                Email
              </VuiTypography>
            </VuiBox>
            <GradientBorder
              minWidth="100%"
              borderRadius={borders.borderRadius.lg}
              padding="1px"
              backgroundImage={radialGradient(
                palette.gradients.borderLight.main,
                palette.gradients.borderLight.state,
                palette.gradients.borderLight.angle
              )}
            >
              <VuiInput
                type="email"
                placeholder="Seu e-mail..."
                value={email}
                sx={({ typography: { size } }) => ({
                  fontSize: size.sm,
                })}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </GradientBorder>
          </VuiBox>
          <VuiBox display="flex" alignItems="center" mb={0.5}>
            <VuiSwitch color="info" checked={accountType === 'business'} onChange={() => setAccountType(accountType === 'personal' ? 'business' : 'personal')} />
            <VuiTypography
              variant="button"
              color="white"
              fontWeight="medium"
              ml={1}
              sx={{ cursor: 'pointer' }}
              onClick={() => setAccountType(accountType === 'personal' ? 'business' : 'personal')}
            >
              Registrar como Pessoa Jurídica
            </VuiTypography>
          </VuiBox>
          {accountType === 'business' ? (
            <>
              <VuiBox mb={0.5}>
                <VuiBox mb={0.5} ml={0.5}>
                  <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                    Nome da Empresa
                  </VuiTypography>
                </VuiBox>
                <GradientBorder
                  minWidth="100%"
                  borderRadius={borders.borderRadius.lg}
                  padding="1px"
                  backgroundImage={radialGradient(
                    palette.gradients.borderLight.main,
                    palette.gradients.borderLight.state,
                    palette.gradients.borderLight.angle
                  )}
                >
                  <VuiInput
                    type="text"
                    placeholder="Nome da sua empresa..."
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                    autoComplete="organization"
                  />
                </GradientBorder>
              </VuiBox>
              <VuiBox mb={0.5}>
                <VuiBox mb={0.5} ml={0.5}>
                  <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                    CNPJ
                  </VuiTypography>
                </VuiBox>
                <GradientBorder
                  minWidth="100%"
                  borderRadius={borders.borderRadius.lg}
                  padding="1px"
                  backgroundImage={radialGradient(
                    palette.gradients.borderLight.main,
                    palette.gradients.borderLight.state,
                    palette.gradients.borderLight.angle
                  )}
                >
                  <VuiInput
                    type="text"
                    placeholder="Seu CNPJ..."
                    value={cnpj}
                    onChange={(e) => setCnpj(e.target.value)}
                    required
                  />
                </GradientBorder>
              </VuiBox>
            </>
          ) : (
            <VuiBox mb={0.5}>
              <VuiBox mb={0.5} ml={0.5}>
                <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                  CPF
                </VuiTypography>
              </VuiBox>
              <GradientBorder
                minWidth="100%"
                borderRadius={borders.borderRadius.lg}
                padding="1px"
                backgroundImage={radialGradient(
                  palette.gradients.borderLight.main,
                  palette.gradients.borderLight.state,
                  palette.gradients.borderLight.angle
                )}
              >
                <VuiInput
                  type="text"
                  placeholder="Seu CPF..."
                  onChange={(e) => setCpf(e.target.value)}
                  required
                />
              </GradientBorder>
            </VuiBox>
          )}
          <VuiBox mb={0.5}>
            <VuiBox mb={0.25} ml={0.5}>
              <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                Senha
              </VuiTypography>
            </VuiBox>
            <GradientBorder
              minWidth="100%"
              borderRadius={borders.borderRadius.lg}
              padding="1px"
              backgroundImage={radialGradient(
                palette.gradients.borderLight.main,
                palette.gradients.borderLight.state,
                palette.gradients.borderLight.angle
              )}
            >
              <VuiInput
                type={showPassword ? "text" : "password"}
                placeholder="Sua senha..."
                sx={({ typography: { size } }) => ({
                  fontSize: size.sm,
                })}
                onChange={handlePasswordChange}
                onFocus={() => setIsPasswordFocused(true)}
                onBlur={() => setIsPasswordFocused(false)}
                required
                autoComplete="new-password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#828396",
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </GradientBorder>
            {isPasswordFocused && (
              <VuiBox p={1}>
                <VuiTypography variant="button" color={passwordRequirements.minLength ? "success" : "error"}>Mínimo 8 caracteres</VuiTypography>
                <VuiTypography variant="button" color={passwordRequirements.uppercase ? "success" : "error"}>Letra maiúscula</VuiTypography>
                <VuiTypography variant="button" color={passwordRequirements.specialChar ? "success" : "error"}>Caractere especial</VuiTypography>
                <VuiTypography variant="button" color={passwordRequirements.number ? "success" : "error"}>Número</VuiTypography>
              </VuiBox>
            )}
          </VuiBox>
          <VuiBox mb={0.5}>
            <VuiBox mb={0.5} ml={0.5}>
              <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                Confirmar Senha
              </VuiTypography>
            </VuiBox>
            <GradientBorder
              minWidth="100%"
              borderRadius={borders.borderRadius.lg}
              padding="1px"
              backgroundImage={radialGradient(
                palette.gradients.borderLight.main,
                palette.gradients.borderLight.state,
                palette.gradients.borderLight.angle
              )}
            >
              <VuiInput
                type={showPassword ? "text" : "password"}
                placeholder="Confirme sua senha..."
                sx={({ typography: { size } }) => ({
                  fontSize: size.sm,
                })}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#828396",
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </GradientBorder>
          </VuiBox>
          {error && (
            <VuiTypography variant="caption" color="error" textAlign="center">
              {error}
            </VuiTypography>
          )}
          <VuiBox mt={2} mb={1}>
            <VuiButton color="info" fullWidth type="submit">
              CRIAR CONTA
            </VuiButton>
          </VuiBox>
          <VuiBox mt={3} textAlign="center">
            <VuiTypography variant="button" color="text" fontWeight="regular">
              Já tem uma conta?{" "}
              <VuiTypography
                component={Link}
                to="/authentication/sign-in"
                variant="button"
                color="white"
                fontWeight="medium"
              >
                Entrar
              </VuiTypography>
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      </GradientBorder>
    </CoverLayout>
  );
}

export default SignUp;